// ============================================================================
// PublicationEditor — editeur de publication avec apercu telephone en direct
// ============================================================================
// Ouvert par « Publier » de l'accueil, a la place du fil (pas de route neuve) :
// a gauche, les etapes en ONGLETS (EditorTabs) — Contenu (titre, message),
// Pieces jointes (visuel, fichiers joints), Diffusion (categorie, audience) —
// chacun marque « Pret » ou « Manquant », avec compteurs et avertissement doux
// de coupe ; a droite, le telephone qui rend le VRAI PublicationCard du fil
// (PhonePreview), toujours visible sur grand ecran. Sous lg, une bascule
// « Ecrire / Apercu » montre l'un ou l'autre.
// Publication : le meme chemin optimiste que l'ancien composeur
// (usePublishPublication) — titre, message, categorie, visibilite, et le
// VISUEL qui part avec eux dans la meme requete (fichier puis ligne au
// serveur). Le visuel n'existe que si `canAttachImage` (verdict serveur :
// publier ET stockage configure) — capability-aware, jamais mort. L'apercu
// montre l'image en object URL local (blob:), le meme contrat que le fil.
// Type et taille sont pre-verifies ici pour ne pas perdre un brouillon ; la
// verite reste SERVEUR (P-UP-2/7), son refus remonte en code court au bandeau.
// Les PIECES JOINTES multiples (images, PDF, .pptx ; mig 285) n'existent que si
// `canAttachFiles` (verdict serveur : publier, stockage ET table presents), se
// voient dans l'apercu et partent APRES la publication, chacune par sa requete
// (usePublishPublication) ; un refus remonte en code court au bandeau.
// La visibilite reste une AFFORDANCE : le serveur re-valide contre le role.

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ConfirmDialog, GsSegmented, PageHeader, GS_COMPONENTS,
} from '@gs/ui';
import type { GsSegmentedOption } from '@gs/ui';
import type { TFunction } from 'i18next';
import {
  ArrowLeft, ArrowRight, ImagePlus, Info, Lock, Paperclip, PenLine, Send, Trash2, TriangleAlert, Users,
} from 'lucide-react';
import {
  PUBLICATION_CATEGORIES,
  type Publication, type PublicationCategory,
} from '../../hooks/useAccueil';
import { usePublishPublication, type OptimisticAuthor } from '../../hooks/useAccueilMutations';
import { AttachmentsField } from './AttachmentsField';
import { EditorTabs, type EditorTab } from './EditorTabs';
import { panelDomId, tabDomId } from './editorTabIds';
import { PhonePreview } from './PhonePreview';
import { BODY_MAX, TITLE_COMFORT_LINES, TITLE_MAX, estimateCut } from './apercu';
import { sortFiles, type Attachment, type Refusal } from './piecesJointes';

/** Miroir de la surface serveur 'publications' (server/blobs/upload.ts) — affordance seulement. */
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const IMAGE_MAX_BYTES = 5 * 1024 * 1024;

type Visibility = 'public' | 'management';
type Pane = 'write' | 'preview';
type TabId = 'content' | 'media' | 'audience';
const TAB = { content: 'content', media: 'media', audience: 'audience' } as const satisfies Record<TabId, TabId>;

const FIELD_IDS = {
  title: 'editor-title',
  body: 'editor-body',
  image: 'editor-image',
  files: 'editor-files',
  category: 'editor-category',
  audience: 'editor-audience',
} as const;

function SoftWarning({ children, tone = 'warn' }: { children: ReactNode; tone?: 'warn' | 'info' }) {
  const Icon = tone === 'warn' ? TriangleAlert : Info;
  return (
    <p className="mt-2 flex items-start gap-2 text-field text-slate-600 dark:text-slate-300">
      <Icon
        size={15}
        aria-hidden
        className={`mt-0.5 shrink-0 ${tone === 'warn' ? 'text-amber-500' : 'text-blue-500'}`}
      />
      <span>{children}</span>
    </p>
  );
}

/** Une carte de champ dans un onglet : titre (libelle du champ s'il en a un), mention, aide. */
function FieldSection({
  id,
  label,
  labelFor,
  tag,
  hint,
  children,
}: {
  id: string;
  label: string;
  /** Le titre de la carte est le <label> du champ (titre, message). */
  labelFor?: string;
  tag?: string;
  hint: string;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={`section-${id}`} className={`${GS_COMPONENTS.card} p-5`}>
      <div className="mb-3">
        <h2 id={`section-${id}`} className="text-body font-semibold text-slate-800 dark:text-slate-100">
          {labelFor ? <label htmlFor={labelFor}>{label}</label> : label}
          {tag ? (
            <span className="ml-2 text-meta font-medium text-slate-400 dark:text-slate-500">{tag}</span>
          ) : null}
        </h2>
        <p className="text-meta text-slate-500 dark:text-slate-400">{hint}</p>
      </div>
      {children}
    </section>
  );
}

export function PublicationEditor({
  author,
  locale,
  t,
  canAttachImage,
  canAttachFiles,
  nextInFeed,
  onClose,
  onError,
}: {
  author: OptimisticAuthor;
  locale: string;
  t: TFunction;
  /** Le visuel peut-il etre joint ? Faux tant que le televersement reel manque. */
  canAttachImage: boolean;
  /** Les pieces jointes multiples — verdict serveur (publier, stockage, table presents). */
  canAttachFiles: boolean;
  /** Premiere publication du fil, montree attenuee sous l'apercu. */
  nextInFeed?: Publication;
  onClose: () => void;
  /** Remonte le code court d'erreur au bandeau de la page (GA-21). */
  onError: (code: string) => void;
}) {
  // Le refus remonte par la mutation elle-meme : l'editeur est deja ferme.
  const publish = usePublishPublication(author, onError);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState<PublicationCategory>('general');
  const [visibility, setVisibility] = useState<Visibility>('public');
  const [image, setImage] = useState<{ url: string; name: string; file: File } | null>(null);
  const [imageError, setImageError] = useState<'notImage' | 'tooLarge' | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [refused, setRefused] = useState<Refusal[]>([]);
  const [activeTab, setActiveTab] = useState<TabId>(TAB.content);
  const [pane, setPane] = useState<Pane>('write');
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const nextAttachmentId = useRef(0);
  const attachmentsRef = useRef<Attachment[]>([]);

  // L'object URL se libere quand l'image change ou que l'editeur se ferme.
  useEffect(() => () => {
    if (image) URL.revokeObjectURL(image.url);
  }, [image]);

  // Les vignettes des pieces jointes : liberees au retrait (removeAttachment)
  // et, pour celles qui restent, a la fermeture de l'editeur.
  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);
  useEffect(() => () => {
    for (const a of attachmentsRef.current) if (a.url) URL.revokeObjectURL(a.url);
  }, []);

  const cut = useMemo(() => estimateCut(title, body), [title, body]);
  const titleOk = title.trim().length > 0;
  const bodyOk = body.trim().length > 0;
  const canSubmit = titleOk && bodyOk && !publish.isPending;
  const dirty = title.trim() !== '' || body.trim() !== '' || image !== null || attachments.length > 0;
  const hasMedia = canAttachImage || canAttachFiles;

  const tabs: Array<EditorTab & { id: TabId }> = [
    { id: TAB.content, label: t('gest:accueil.editor.tabs.content'), icon: PenLine, ready: titleOk && bodyOk },
    ...(hasMedia
      ? [{
          id: TAB.media,
          label: t('gest:accueil.editor.tabs.media'),
          icon: Paperclip,
          ready: true,
          count: attachments.length + (image ? 1 : 0),
        }]
      : []),
    { id: TAB.audience, label: t('gest:accueil.editor.tabs.audience'), icon: Users, ready: true },
  ];

  const missing = [
    ...(titleOk ? [] : [t('gest:accueil.editor.stepTitle.label')]),
    ...(bodyOk ? [] : [t('gest:accueil.editor.stepBody.label')]),
  ];
  const missingList = new Intl.ListFormat(locale.startsWith('en') ? 'en-CA' : 'fr-CA', {
    type: 'conjunction',
  }).format(missing);

  const categoryOptions: GsSegmentedOption[] = PUBLICATION_CATEGORIES.map((c) => ({
    value: c,
    label: t(`gest:accueil.categories.${c}`),
  }));
  const audienceOptions: GsSegmentedOption[] = [
    { value: 'public', label: t('gest:accueil.composer.visibilityPublic') },
    { value: 'management', label: t('gest:accueil.composer.visibilityManagement') },
  ];
  const paneOptions: GsSegmentedOption[] = [
    { value: 'write', label: t('gest:accueil.editor.paneWrite') },
    { value: 'preview', label: t('gest:accueil.editor.panePreview') },
  ];

  function goToTab(id: TabId) {
    setActiveTab(id);
    document.getElementById(tabDomId(id))?.focus();
  }

  function chooseImage(file: File | undefined) {
    if (!file) return;
    if (!IMAGE_TYPES.includes(file.type)) {
      setImageError('notImage');
      return;
    }
    if (file.size > IMAGE_MAX_BYTES) {
      setImageError('tooLarge');
      return;
    }
    setImageError(null);
    setImage({ url: URL.createObjectURL(file), name: file.name, file });
  }

  function addAttachments(files: File[]) {
    if (files.length === 0) return;
    const sorted = sortFiles(attachments, files);
    // Les object URL se creent ICI, une fois — jamais dans un updater d'etat,
    // que le mode strict rejoue.
    const added: Attachment[] = sorted.accepted.map(({ file, kind }) => ({
      id: `pj-${++nextAttachmentId.current}`,
      kind,
      name: file.name,
      size: file.size,
      file,
      url: kind === 'image' ? URL.createObjectURL(file) : undefined,
    }));
    setRefused(sorted.refused);
    if (added.length > 0) setAttachments((prev) => [...prev, ...added]);
  }

  function removeAttachment(id: string) {
    const gone = attachments.find((a) => a.id === id);
    if (gone?.url) URL.revokeObjectURL(gone.url);
    setRefused([]);
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }

  function requestClose() {
    if (dirty) setConfirmDiscard(true);
    else onClose();
  }

  function submit() {
    if (!canSubmit) return;
    // Optimiste : le fil montre deja la publication au retour ; un refus
    // serveur la retire (rollback) et remonte au bandeau de la page.
    // Les pieces jointes suivent la creation, dans l'ordre de la liste.
    publish.mutate({
      title: title.trim(),
      body: body.trim(),
      category,
      visibility,
      image: image?.file ?? null,
      attachments: canAttachFiles ? attachments.map((a) => ({ file: a.file, kind: a.kind })) : [],
    });
    onClose();
  }

  const draft = {
    title: title.trim() || t('gest:accueil.editor.preview.emptyTitle'),
    body: body.trim() || t('gest:accueil.editor.preview.emptyBody'),
    category,
    authorName: author.name,
    authorEmail: author.email,
    postedAt: new Date().toISOString(),
    reactions: { like: 0, love: 0, celebrate: 0 },
    myReaction: null,
    viewCount: 0,
    commentCount: 0,
  };

  const AudienceIcon = visibility === 'public' ? Users : Lock;

  /** Le bouton « Suivant : <onglet> » au pied d'un onglet, sauf le dernier. */
  function nextButton(id: TabId) {
    const index = tabs.findIndex((tab) => tab.id === id);
    const next = tabs[index + 1];
    if (!next) return null;
    return (
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => goToTab(next.id)}
          className={`${GS_COMPONENTS.btnSecondary} inline-flex touch-manipulation items-center gap-1.5`}
        >
          {t('gest:accueil.editor.tabs.next', { tab: next.label })}
          <ArrowRight size={15} aria-hidden />
        </button>
      </div>
    );
  }

  /** Panneau d'onglet : monte en permanence (rien ne se perd), cache s'il n'est pas actif. */
  function panel(id: TabId, children: ReactNode) {
    return (
      <div
        key={id}
        id={panelDomId(id)}
        role="tabpanel"
        aria-labelledby={tabDomId(id)}
        className={`space-y-3.5 ${activeTab === id ? '' : 'hidden'}`.trim()}
      >
        {children}
        {nextButton(id)}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <button
        type="button"
        onClick={requestClose}
        className="inline-flex touch-manipulation items-center gap-1.5 text-field font-medium text-slate-500 transition-colors hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100"
      >
        <ArrowLeft size={15} aria-hidden />
        {t('gest:accueil.editor.back')}
      </button>

      <PageHeader
        title={t('gest:accueil.editor.heading')}
        description={t('gest:accueil.editor.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={requestClose}
              className={`${GS_COMPONENTS.btnSecondary} touch-manipulation`}
            >
              {t('gest:accueil.composer.cancel')}
            </button>
            <button
              type="submit"
              form="publication-editor"
              disabled={!canSubmit}
              className={`${GS_COMPONENTS.btnAction} inline-flex items-center gap-2`}
            >
              <Send size={15} aria-hidden />
              {t('gest:accueil.composer.publish')}
            </button>
          </div>
        }
      />

      {!titleOk || !bodyOk ? (
        <p className="text-field text-slate-500 dark:text-slate-400" role="status">
          {t('gest:accueil.editor.missing', { fields: missingList })}
        </p>
      ) : null}

      <div className="lg:hidden">
        <GsSegmented
          ariaLabel={t('gest:accueil.editor.paneAria')}
          options={paneOptions}
          value={pane}
          onChange={(v) => setPane(v as Pane)}
          fullWidth
        />
      </div>

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_auto]">
        {/* ── Ecrire : les onglets d'etapes, puis le panneau de l'onglet actif ─ */}
        <form
          id="publication-editor"
          aria-label={t('gest:accueil.editor.heading')}
          className={`space-y-3.5 ${pane === 'preview' ? 'hidden lg:block' : ''}`.trim()}
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <EditorTabs
            tabs={tabs}
            active={activeTab}
            onChange={(id) => setActiveTab(id as TabId)}
            ariaLabel={t('gest:accueil.editor.stepsAria')}
            readyLabel={t('gest:accueil.editor.tabs.ready')}
            missingLabel={t('gest:accueil.editor.tabs.missing')}
          />

          {panel(TAB.content, (
            <>
              <FieldSection
                id="title"
                label={t('gest:accueil.editor.stepTitle.label')}
                labelFor={FIELD_IDS.title}
                tag={t('gest:accueil.editor.required')}
                hint={t('gest:accueil.editor.stepTitle.hint')}
              >
                <input
                  id={FIELD_IDS.title}
                  autoFocus
                  maxLength={TITLE_MAX}
                  placeholder={t('gest:accueil.composer.titlePlaceholder')}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className={GS_COMPONENTS.input}
                />
                <div className="mt-1.5 flex justify-end text-meta tabular-nums text-slate-400 dark:text-slate-500">
                  {t('gest:accueil.editor.counter', { length: title.length, max: TITLE_MAX })}
                </div>
                {cut.titleLong && (
                  <SoftWarning>
                    {t('gest:accueil.editor.warnings.titleLong', {
                      lines: cut.titleLines,
                      comfort: TITLE_COMFORT_LINES,
                    })}
                  </SoftWarning>
                )}
              </FieldSection>

              <FieldSection
                id="body"
                label={t('gest:accueil.editor.stepBody.label')}
                labelFor={FIELD_IDS.body}
                tag={t('gest:accueil.editor.required')}
                hint={t('gest:accueil.editor.stepBody.hint')}
              >
                <textarea
                  id={FIELD_IDS.body}
                  rows={8}
                  maxLength={BODY_MAX}
                  placeholder={t('gest:accueil.composer.bodyPlaceholder')}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  className={GS_COMPONENTS.input}
                />
                <div className="mt-1.5 flex justify-end text-meta tabular-nums text-slate-400 dark:text-slate-500">
                  {t('gest:accueil.editor.counter', {
                    length: body.length,
                    max: BODY_MAX.toLocaleString(locale.startsWith('en') ? 'en-CA' : 'fr-CA'),
                  })}
                </div>
                {cut.bodyCut && (
                  <SoftWarning>
                    {t('gest:accueil.editor.warnings.bodyCut')}{' '}
                    <span className="italic">
                      {t('gest:accueil.editor.warnings.excerpt', { excerpt: cut.visibleExcerpt })}
                    </span>
                  </SoftWarning>
                )}
              </FieldSection>
            </>
          ))}

          {hasMedia && panel(TAB.media, (
            <>
              {canAttachImage && (
                <FieldSection
                  id="image"
                  label={t('gest:accueil.editor.stepImage.label')}
                  tag={t('gest:accueil.editor.optional')}
                  hint={t('gest:accueil.editor.stepImage.hint')}
                >
                  <div id={FIELD_IDS.image}>
                    <input
                      id="editor-image-file"
                      type="file"
                      accept={IMAGE_TYPES.join(',')}
                      className="sr-only"
                      aria-label={t('gest:accueil.editor.image.inputAria')}
                      onChange={(e) => {
                        chooseImage(e.target.files?.[0]);
                        e.target.value = '';
                      }}
                    />
                    {image ? (
                      <div className="flex items-center gap-3">
                        <img
                          src={image.url}
                          alt={t('gest:accueil.editor.image.thumbAlt')}
                          className="h-16 w-16 shrink-0 rounded-lg border border-slate-200 object-cover dark:border-slate-700"
                        />
                        <p className="min-w-0 flex-1 truncate text-field text-slate-600 dark:text-slate-300">
                          {image.name}
                        </p>
                        <label
                          htmlFor="editor-image-file"
                          className={`${GS_COMPONENTS.btnSecondary} cursor-pointer touch-manipulation`}
                        >
                          {t('gest:accueil.editor.image.replace')}
                        </label>
                        <button
                          type="button"
                          onClick={() => setImage(null)}
                          className={`${GS_COMPONENTS.btnSecondary} inline-flex touch-manipulation items-center gap-1.5`}
                        >
                          <Trash2 size={14} aria-hidden />
                          {t('gest:accueil.editor.image.remove')}
                        </button>
                      </div>
                    ) : (
                      <label
                        htmlFor="editor-image-file"
                        className="flex cursor-pointer touch-manipulation flex-col items-center gap-1.5 rounded-xl border border-dashed border-slate-300 px-4 py-6 text-center transition-colors hover:border-brand-400 hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700/40"
                      >
                        <ImagePlus size={24} aria-hidden className="text-slate-400 dark:text-slate-500" />
                        <span className="text-field font-medium text-slate-700 dark:text-slate-200">
                          {t('gest:accueil.editor.image.choose')}
                        </span>
                        <span className="text-meta text-slate-400 dark:text-slate-500">
                          {t('gest:accueil.editor.image.formats')}
                        </span>
                      </label>
                    )}
                    {imageError && (
                      <p role="alert" className="mt-2 text-field text-red-600 dark:text-red-400">
                        {imageError === 'tooLarge'
                          ? t('gest:accueil.editor.image.tooLarge')
                          : t('gest:accueil.editor.image.notImage')}
                      </p>
                    )}
                  </div>
                </FieldSection>
              )}

              {canAttachFiles && (
                <FieldSection
                  id="files"
                  label={t('gest:accueil.editor.stepFiles.label')}
                  tag={t('gest:accueil.editor.optional')}
                  hint={t('gest:accueil.editor.stepFiles.hint')}
                >
                  <div id={FIELD_IDS.files}>
                    <AttachmentsField
                      attachments={attachments}
                      refused={refused}
                      onAdd={addAttachments}
                      onRemove={removeAttachment}
                      locale={locale}
                      t={t}
                    />
                  </div>
                </FieldSection>
              )}
            </>
          ))}

          {panel(TAB.audience, (
            <>
              <FieldSection
                id="category"
                label={t('gest:accueil.editor.stepCategory.label')}
                hint={t('gest:accueil.editor.stepCategory.hint')}
              >
                <div id={FIELD_IDS.category}>
                  <GsSegmented
                    variant="scrollable"
                    ariaLabel={t('gest:accueil.editor.stepCategory.label')}
                    options={categoryOptions}
                    value={category}
                    onChange={(v) => setCategory(v as PublicationCategory)}
                    className="pb-1"
                  />
                </div>
              </FieldSection>

              <FieldSection
                id="audience"
                label={t('gest:accueil.editor.stepAudience.label')}
                hint={t('gest:accueil.editor.stepAudience.hint')}
              >
                <div id={FIELD_IDS.audience}>
                  <GsSegmented
                    ariaLabel={t('gest:accueil.editor.stepAudience.label')}
                    options={audienceOptions}
                    value={visibility}
                    onChange={(v) => setVisibility(v as Visibility)}
                  />
                  <p className="mt-2 text-field text-slate-500 dark:text-slate-400">
                    {visibility === 'public'
                      ? t('gest:accueil.editor.audience.publicHint')
                      : t('gest:accueil.editor.audience.managementHint')}
                  </p>
                </div>
              </FieldSection>
            </>
          ))}
        </form>

        {/* ── Apercu : le telephone, colle en haut pendant qu'on ecrit ────── */}
        <aside className={`lg:sticky lg:top-4 ${pane === 'write' ? 'hidden lg:block' : ''}`.trim()}>
          <p className="mb-3 flex items-center justify-center gap-1.5 text-field font-medium text-slate-600 dark:text-slate-300">
            <AudienceIcon size={15} aria-hidden className="text-slate-400" />
            {t('gest:accueil.editor.preview.audience', {
              audience:
                visibility === 'public'
                  ? t('gest:accueil.composer.visibilityPublic')
                  : t('gest:accueil.composer.visibilityManagement'),
            })}
          </p>
          <PhonePreview
            draft={draft}
            imageObjectUrl={image?.url}
            attachments={attachments}
            nextInFeed={nextInFeed}
            locale={locale}
            t={t}
          />
        </aside>
      </div>

      <ConfirmDialog
        open={confirmDiscard}
        title={t('gest:accueil.editor.discard.title')}
        message={
          attachments.length > 0
            ? t('gest:accueil.editor.discard.messageFiles')
            : t('gest:accueil.editor.discard.message')
        }
        confirmLabel={t('gest:accueil.editor.discard.confirm')}
        cancelLabel={t('gest:accueil.editor.discard.cancel')}
        variant="warning"
        onConfirm={() => {
          setConfirmDiscard(false);
          onClose();
        }}
        onCancel={() => setConfirmDiscard(false)}
      />
    </div>
  );
}
